import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Rocket, Check, Moon, Sun, LogOut } from "lucide-react";
import { shouldRedirectToLogin } from './utils/oauthRedirect';
import { OAUTH_CONFIG } from './config/oauth';
import { authService } from "./services/auth";
import { httpRequest } from "./utils/httpClient";
import { API_BASE } from './config/api';
import { debugLog } from './utils/debugLogger';
import HelpModal from './components/HelpModal';
import WhyButton from './components/WhyButton';
import useHelpModal from './hooks/useHelpModal';
import Simplify from "./components/features/Simplify";
import ProfileSidebar from "./components/features/ProfileSidebar";
import Login from "./components/features/Login";
import OAuthCallback from "./components/features/OAuthCallback";
import AdminGames from "./components/features/AdminGames";
import FeedbackAdmin from "./components/features/FeedbackAdmin";
import ABTestAdmin from "./components/features/ABTestAdmin";
import FeatureBlacklistAdmin from "./components/features/FeatureBlacklistAdmin";
import SurveyAdmin from "./components/features/SurveyAdmin";
import UserAdmin from "./components/features/UserAdmin";
import AdminDataUpdates from "./components/admin/AdminDataUpdates";
import AnalyticsDashboard from "./components/admin/AnalyticsDashboard";
import AdminDashboardPage from "./components/admin/AdminDashboardPage";
import UserActionStats from "./components/admin/UserActionStats";
import AdminBggHotnessSimilar from "./components/admin/AdminBggHotnessSimilar";
import Collection from "./components/features/Collection";
import Explore from "./components/features/Explore";
import PastSessions from "./components/features/PastSessions";
import SharedChart from "./components/features/SharedChart";
import SessionSharePage from "./components/features/SessionSharePage";
import HistoryShareRedirect from "./components/features/HistoryShareRedirect";
import SessionInvitePage from "./components/features/SessionInvitePage";
import ReactivateAccountPage from "./components/features/ReactivateAccountPage";
import PWAInstallPrompt from "./components/common/PWAInstallPrompt";
import AppInstallSnackbar from "./components/common/AppInstallSnackbar";
import UsernameReminderBanner from "./components/common/UsernameReminderBanner";
import AppUpdateBanner from "./components/common/AppUpdateBanner";
import { isAndroid, isInstalled } from "./utils/pwaInstall";
import { installPrompts } from "./config/installPrompts";
// import CookieConsentBanner from "./components/common/CookieConsentBanner";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { initializeMobileNotifications, updateAppBadge, showGameNotification, subscribeToNotifications } from "./services/notifications";
import SurveyRoute from "./routes/SurveyRoute";
import TypeformSurvey from "./routes/TypeformSurvey";
import SurveyAnalytics from "./routes/admin/SurveyAnalytics";
import LegalPage from "./components/legal/LegalPage";
import LegalConsentPopup from "./components/legal/LegalConsentPopup";
import { getConsentStatus } from "./services/legal";
import { canAccessAdminRoutes, getLoginRedirectPath, isEmailVerified } from "./utils/routeGuards";
import VerifyEmail from "./components/auth/VerifyEmail";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import { TIMELINE_ICON, COLLECTION_ICON, JET_SET_GO_ICON, ADMIN_ICON, GAME_ICON, SETTINGS_ICON, NOTIFICATION_ICON, USER_ICON, MOBILE_PHONE_ICON } from "./constants/icons";
import { SidebarZIndexProvider } from "./contexts/SidebarZIndexContext";
import { AnalyticsProvider, useAnalytics } from "./contexts/AnalyticsContext";
import { analyticsService } from "./services/analytics";
import { ChatbotProvider, ChatbotButton, ChatbotSidebar } from "./components/chatbot";
import "./styles/index.css";
import "./styles/dark-mode.css";
import "./styles/mobile-safe-area.css";
import logger from './utils/logger';
import { initializeLogger, setUserContext } from './utils/loggerInit';
import QuickStartModal from './components/onboarding/QuickStartModal';
import { useQuickStart } from './hooks/useQuickStart';
import { isFeatureEnabled } from './config/features';

function AppShell({
  children,
  user,
  darkMode,
  setDarkMode,
  notificationButtonRef,
  notificationListRef,
  requestNotificationPermission,
  setShowNotifications,
  showNotifications,
  notificationPanelRef,
  _notificationPermission,
  notifications,
  totalNotificationCount,
  acknowledgeNotificationReminder,
  snoozeNotification,
  _removeNotification,
  _upcomingReminders,
  _completeReminder,
  _clearNotifications,
  setShowProfileSidebar,
  showProfilePanel,
  setShowProfilePanel,
  handleLogout,
  hasMoreNotifications,
  refreshNotifications,
  openModal,
  showWhyModal,
  closeModal,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPageView } = useAnalytics();
  const [navLabelsVisible, setNavLabelsVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Show labels on scroll-up / at top, hide on scroll-down
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      if (y <= 10) {
        setNavLabelsVisible(true);
      } else if (y > lastScrollY.current + 4) {
        setNavLabelsVisible(false);
      } else if (y < lastScrollY.current - 4) {
        setNavLabelsVisible(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname, trackPageView]);

  // Check if current page is an admin page
  const isAdminPage = location.pathname.startsWith('/admin');

  // Format notification time
  const formatNotificationTime = (createdAt) => {
    const now = new Date();
    const notificationTime = new Date(createdAt);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notificationTime.toLocaleDateString();
  };

  return (
    <div className={`App${Capacitor.isNativePlatform() ? " app-native" : ""}`}>
      <nav
        className={`app-nav${navLabelsVisible ? ' nav-labels-visible' : ''}`}
      >
        <div className="nav-brand">
          <button
            onClick={() => navigate("/")}
            aria-label="Home"
            className="nav-brand-button"
            style={{
              backgroundColor: process.env.REACT_APP_ENV === 'development' ? '#ef4444' : undefined,
            }}
          >
            <img src="/logo192.png" alt="Pista" className="nav-logo" />
            <div className="nav-brand-text">
              <div className="nav-brand-title">Pista</div>
              <div className="nav-brand-subtitle">by VoyagerBug</div>
            </div>
          </button>
        </div>
        {/* Navigation Links */}
        <div className="nav-links">
          {user ? (
            <>
              <Link to="/collection" title="Library" className={location.pathname === "/collection" ? "active" : ""}>{COLLECTION_ICON}<span className="nav-label">Library</span></Link>
              <Link to="/quick-decision" title="Jet Set Go" className={`${location.pathname === "/quick-decision" ? "active" : ""} main-nav-icon`}>{JET_SET_GO_ICON}<span className="nav-label">Jet Set Go</span></Link>
              <Link to="/past-sessions" title="Timeline" className={location.pathname === "/past-sessions" ? "active" : ""}>{TIMELINE_ICON}<span className="nav-label">Timeline</span></Link>
              {user.is_admin && <Link to="/admin" title="Admin Controls" className={location.pathname.startsWith("/admin") ? "active" : ""}>{ADMIN_ICON}<span className="nav-label">Admin</span></Link>}
              {installPrompts.showTopNavInstall && !Capacitor.isNativePlatform() && !isInstalled() && isAndroid() && installPrompts.googlePlayUrl && (
                <a
                  href={installPrompts.googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Get Pista on Google Play"
                  className="go-to-app-link"
                >
                  {MOBILE_PHONE_ICON}
                  <span className="nav-label">Get app</span>
                </a>
              )}
            </>
          ) : (
            <>
              <Link to="/quick-decision" title="Jet Set Go" className={`${location.pathname === "/quick-decision" ? "active" : ""} main-nav-icon`}>{JET_SET_GO_ICON}<span className="nav-label">Jet Set Go</span></Link>
              {installPrompts.showTopNavInstall && !Capacitor.isNativePlatform() && !isInstalled() && isAndroid() && installPrompts.googlePlayUrl && (
                <a
                  href={installPrompts.googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Get Pista on Google Play"
                  className="go-to-app-link"
                >
                  {MOBILE_PHONE_ICON}
                  <span className="nav-label">Get app</span>
                </a>
              )}
            </>
          )}
        </div>

        {/* Profile Icon - Separate from main navigation */}
        {user ? (
          <div className="notification-wrapper">
            <button
              onClick={() => {
                setShowProfileSidebar(true);
                setShowProfilePanel(false);
              }}
              className="profile-icon-button"
              title="Profile"
              aria-label="Open profile"
            >
              <div className="pista-profile-icon">
                {user?.username?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              {totalNotificationCount > 0 && (
                <span className="notification-badge">{totalNotificationCount}</span>
              )}
              <span className="nav-label">Profile</span>
            </button>
            {showProfilePanel && user && (
              <div ref={notificationPanelRef} className="profile-panel">
                <div className="profile-panel-header">
                  <span>Profile</span>
                  <button
                    type="button"
                    onClick={() => setShowProfilePanel(false)}
                    className="profile-panel-close"
                    aria-label="Close profile panel"
                  >
                    ×
                  </button>
                </div>
                <div className="profile-panel-content">
                  <div className="profile-panel-item">
                    <button
                      ref={notificationButtonRef}
                      onClick={async () => {
                        await requestNotificationPermission();
                        setShowNotifications((prev) => !prev);
                      }}
                      className="profile-panel-button"
                      title="Notifications"
                    >
                      {NOTIFICATION_ICON} Notifications
                      {totalNotificationCount > 0 && (
                        <span className="notification-badge">{totalNotificationCount}</span>
                      )}
                    </button>
                    {showNotifications && (
                      <div ref={notificationListRef} className="notification-list">
                        {notifications.length === 0 ? (
                          <div className="notification-empty">
                            No notifications
                          </div>
                        ) : (
                          <>
                            {notifications.map((item) => (
                              <div key={item.id} className="notification-item">
                                <div className="notification-content">
                                  <div className="notification-message">
                                    {item.message}
                                  </div>
                                  {item.game_name && (
                                    <div className="notification-game">
                                      {GAME_ICON} {item.game_name}
                                    </div>
                                  )}
                                  <div className="notification-time">
                                    {formatNotificationTime(item.created_at)}
                                  </div>
                                </div>
                                <div className="notification-actions">
                                  {item.type === 'reminder' && !item.acknowledged && (
                                    <button
                                      type="button"
                                      onClick={() => acknowledgeNotificationReminder(item)}
                                      title="Mark reminder done"
                                    >
                                      <Check size={14} />
                                    </button>
                                  )}
                                  {item.reminder_id && (
                                    <>
                                      <button
                                        type="button"
                                        className="notification-action"
                                        onClick={() => acknowledgeNotificationReminder(item)}
                                        title="Mark reminder done"
                                      >
                                        Done
                                      </button>
                                      <button
                                        type="button"
                                        className="notification-action secondary"
                                        onClick={() => snoozeNotification(item.id, 60)}
                                        title="Snooze for 1 hour"
                                      >
                                        Snooze
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                            {hasMoreNotifications && (
                              <div className="notification-footer">
                                <button
                                  type="button"
                                  onClick={refreshNotifications}
                                  className="notification-refresh"
                                >
                                  Refresh
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="profile-panel-item">
                    <button
                      onClick={() => {
                        setShowProfileSidebar(true);
                        setShowProfilePanel(false);
                      }}
                      className="profile-panel-button"
                      title="Edit Profile"
                    >
                      {SETTINGS_ICON} Settings
                    </button>
                  </div>
                  <div className="profile-panel-item">
                    <button
                      onClick={() => {
                        setDarkMode(!darkMode);
                        setShowProfilePanel(false);
                      }}
                      className="profile-panel-button"
                      title="Toggle Dark Mode"
                    >
                      {darkMode ? (<> <Sun size={16} /> Light Mode</>) : (<> <Moon size={16} /> Dark Mode</>)}
                    </button>
                  </div>
                  <div className="profile-panel-item">
                    <button
                      onClick={async () => {
                        await handleLogout();
                        setShowProfilePanel(false);
                      }}
                      className="profile-panel-button profile-panel-logout"
                      title="Logout"
                    >
                      <LogOut size={16} /> Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ marginRight: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => window.location.href = '/login?register=true'}
                  className="signup-button"
                  aria-label="Sign up"
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: 'var(--pista-green-primary, #2E7D32)',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    height: '2.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span className="signup-text">Sign Up</span>
                  <span className="signup-icon" aria-hidden="true">
                    <Rocket size={20} strokeWidth={2.5} />
                  </span>
                  <span className="nav-label signup-label">Sign Up</span>
                </button>
                <WhyButton onClick={openModal} />
              </span>
              <button
                onClick={() => window.location.href = '/login'}
                className="login-button"
                style={{ whiteSpace: 'nowrap', height: '2.75rem' }}
              >
                <span className="login-text">
                  Login
                </span>
                <span className="login-icon">
                  {USER_ICON}
                </span>
                <span className="nav-label login-label">Login</span>
              </button>
            </div>
            {/* App button hidden per product decision */}
            {false && !Capacitor.isNativePlatform() && (
              <button
                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.pista.app', '_blank', 'noopener,noreferrer')}
                className="go-to-app-button"
                aria-label="Get the mobile app on Google Play"
              >
                {MOBILE_PHONE_ICON}
              </button>
            )}
          </>
        )}
      </nav>

      <main className="app-content">
        {user?.is_admin && isAdminPage && (
          <div className="admin-controls-bar">
            <div className="admin-controls-title">Admin Controls</div>
            <div className="admin-controls-links">
              <Link to="/admin/dashboard">Dashboard</Link>
              <Link to="/admin">Games</Link>
              <Link to="/admin/feedback">Feedback</Link>
              <Link to="/admin/data-updates">Data Updates</Link>
              <Link to="/admin/analytics">Analytics</Link>
              <Link to="/admin/user-actions">User Actions</Link>
              <Link to="/admin/ab-test">A/B Tests</Link>
              <Link to="/admin/feature-blacklist">Feature Blacklist</Link>
              <Link to="/admin/surveys">Surveys</Link>
              <Link to="/admin/survey-analytics">Survey Analytics</Link>
              <Link to="/admin/users">Users</Link>
              <Link to="/admin/bgg-hotness-similar">BGG Hotness</Link>
            </div>
          </div>
        )}
        {children}
      </main>

      {installPrompts.showHomeSnackbar && <AppInstallSnackbar user={user} />}
      <HelpModal isOpen={showWhyModal} onClose={closeModal} />
    </div>
  );
}

function VerifiedRoute({ user, children }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isEmailVerified(user)) {
    return (
      <Navigate
        to={`/verify-email?mode=pending&email=${encodeURIComponent(user.email || "")}`}
        replace
      />
    );
  }
  return children;
}

function App({ _children }) {
  // Initialize logger on app startup
  useEffect(() => {
    initializeLogger();
  }, []);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalConsentPrompt, setLegalConsentPrompt] = useState(null);
  const { track } = useAnalytics();
  const prevUserRef = useRef(null);

  // Update logger user context when user changes
  useEffect(() => {
    setUserContext(user);
  }, [user]);

  // Check legal consent whenever the user changes (login or app load).
  useEffect(() => {
    if (!user) {
      setLegalConsentPrompt(null);
      return undefined;
    }
    let cancelled = false;
    getConsentStatus()
      .then((status) => {
        if (cancelled) return;
        if (!status.has_consented && status.latest_version) {
          setLegalConsentPrompt({
            version: status.latest_version,
            summary: status.summary,
            effectiveDate: status.effective_date,
          });
        } else {
          setLegalConsentPrompt(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logger.warn('Failed to check legal consent status:', err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Track anonymous -> logged-in conversion and page views
  useEffect(() => {
    if (user && !prevUserRef.current) {
      track('anonymous_to_logged_in', { user_id: user.id });
    }
    prevUserRef.current = user;
  }, [user, track]);
  const [showProfileSidebar, setShowProfileSidebar] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [focusUsernameOnProfileOpen, setFocusUsernameOnProfileOpen] = useState(false);
  const { showWhyModal, openModal, closeModal } = useHelpModal();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState(null);
  const [showNotificationPermissionPrompt, setShowNotificationPermissionPrompt] = useState(false);
  const [notificationOffset, setNotificationOffset] = useState(0);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const notificationPanelRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationListRef = useRef(null);
  const notificationsRef = useRef([]);
  const notificationsLoadedRef = useRef(false);
  const notifiedIdsRef = useRef(new Set());
  const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
  const [darkMode, setDarkMode] = useState(() => {
    try {
      // Check if user is logged in by checking for auth token
      const isUserLoggedIn = !!localStorage.getItem("token");

      // For non-logged in users, always default to light mode
      if (!isUserLoggedIn) {
        return false;
      }

      // For logged in users, check localStorage or system preference
      const saved = localStorage.getItem("darkMode");
      if (saved !== null) return saved === "true";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch(e) {
      return false; // Default to light mode on error
    }
  });

  // Quick Start onboarding state
  const quickStart = useQuickStart(user);

  // Existing users (Quick Start already completed) whose username still
  // matches their email get a dismissible nudge to personalize it.
  const quickStartCompleted = quickStart.status?.onboarding_preferences?.quick_start_completed === true;
  const usernameMatchesEmail = !!(user?.username && user?.email && user.username === user.email);
  const showUsernameReminder = quickStartCompleted && usernameMatchesEmail;

  // Expose Quick Start debug functions globally for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugQuickStart = {
        check: quickStart.checkAndShow,
        forceRecheck: quickStart.forceRecheck,
        reset: quickStart.resetCheck,
        fullReset: quickStart.reset,
        getState: () => ({
          isOpen: quickStart.isOpen,
          status: quickStart.status,
          currentScreen: quickStart.currentScreen,
          isLoading: quickStart.isLoading,
          error: quickStart.error,
        }),
        getSessionStorage: () => ({
          quickStartShown: sessionStorage.getItem('quick_start_shown_this_session'),
          quickStartState: sessionStorage.getItem('quick_start_state'),
        }),
        clearSession: () => {
          sessionStorage.removeItem('quick_start_shown_this_session');
          sessionStorage.removeItem('quick_start_state');
        },
      };
    }
  }, [quickStart]);

  useEffect(() => {
    try {
      // Apply dark mode theme
      document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
      localStorage.setItem("darkMode", darkMode.toString());
    } catch(e) {
      // Ignore errors
    }
  }, [darkMode]);

  const requestNotificationPermission = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    if (Notification.permission !== "default") {
      setNotificationPermission(Notification.permission);
      return Notification.permission;
    }

    // Mobile-specific handling
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      // On mobile, provide additional guidance if permission is denied
      if (isMobile && permission === 'denied') {
        logger.warn('Notification permission denied on mobile. User may need to enable in browser settings.');
        // Optionally show a user-friendly message about enabling notifications in settings
      }

      return permission;
    } catch (err) {
      logger.error('Error requesting notification permission:', err);
      // On mobile browsers, permission requests might fail or be ignored
      if (isMobile) {
        logger.warn('Mobile browser detected - notification permission may need to be enabled manually in settings');
      }
      return Notification.permission;
    }
  }, []);

  const showSystemNotification = useCallback((notification) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (!notification?.message) return;

    const tag = notification.reminder_id ? `reminder-${notification.reminder_id}` : `notification-${notification.id}`;

    try {
      // Enhanced notification options for better mobile experience
      const notificationOptions = {
        body: notification.message || "",
        tag,
        icon: "/favicon.png", // Add app icon for better visibility
        badge: "/favicon.png", // Small icon for notification bar
        requireInteraction: false, // Don't require user interaction on mobile
        silent: false, // Ensure sound/vibration on mobile
      };

      // eslint-disable-next-line no-new
      new Notification(notification.title || "Reminder", notificationOptions);
    } catch (err) {
      logger.error('Error showing system notification:', err);
      // Fallback for mobile browsers that might have stricter notification policies
      try {
        // eslint-disable-next-line no-new
        new Notification(notification.title || "Reminder", {
          body: notification.message || "",
          tag,
        });
      } catch (fallbackErr) {
        logger.error('Fallback notification also failed:', fallbackErr);
      }
    }
  }, []);

  const base64ToUint8Array = useCallback((base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  const ensurePushSubscription = useCallback(async () => {
    if (!user) return;
    if (!vapidPublicKey) return;
    if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (notificationPermission !== "granted") return;

    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64ToUint8Array(vapidPublicKey),
        });
      }
      await httpRequest(`${API_BASE}/notifications/subscribe`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscription),
      });
    } catch (err) {
      // ignore subscription errors
    }
  }, [user, vapidPublicKey, notificationPermission, base64ToUint8Array]);

  useEffect(() => {
    if (!user) return;
    if (notificationPermission !== "granted") return;
    ensurePushSubscription();
  }, [user, notificationPermission, ensurePushSubscription]);

  const removeNotification = async (id) => {
    if (!user) return;
    try {
      await httpRequest(`${API_BASE}/notifications/${id}`, {
        method: "DELETE",
        headers: authService.getAuthHeaders(),
      });
    } catch (err) {
      // ignore
    } finally {
      setNotifications((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const clearNotifications = async () => {
    if (!user) return;
    try {
      await httpRequest(`${API_BASE}/notifications/clear`, {
        method: "POST",
        headers: authService.getAuthHeaders(),
      });
    } catch (err) {
      // ignore
    } finally {
      setNotifications([]);
    }
  };

  const completeReminder = async (reminderId) => {
    if (!user) return;
    try {
      await httpRequest(`${API_BASE}/reminders/${reminderId}/complete`, {
        method: "PUT",
        headers: authService.getAuthHeaders(),
      });
    } catch (err) {
      // ignore
    } finally {
      refreshReminders();
      refreshNotifications();
    }
  };

  const snoozeNotification = async (notificationId, minutes = 60) => {
    if (!user) return;
    try {
      await httpRequest(`${API_BASE}/notifications/${notificationId}/snooze`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ minutes }),
      });
    } catch (err) {
      // ignore
    } finally {
      refreshReminders();
      refreshNotifications();
    }
  };

  const acknowledgeNotificationReminder = async (notification) => {
    if (!notification?.reminder_id) {
      await removeNotification(notification.id);
      return;
    }
    await completeReminder(notification.reminder_id);
    await removeNotification(notification.id);
  };

  const totalNotificationCount = notifications.length;

  // Update browser tab title and app badge with notification count
  useEffect(() => {
    updateAppBadge(totalNotificationCount);
  }, [totalNotificationCount]);

  // Initialize mobile notifications when user logs in
  useEffect(() => {
    if (user) {
      initializeMobileNotifications().then(({ granted, setup }) => {
        debugLog('Mobile notifications initialized:', { granted, setup });
        setNotificationPermission(granted);

        // Notification permission prompt disabled — users can enable via Settings
        // if (!granted && !Capacitor.isNativePlatform()) {
        //   setShowNotificationPermissionPrompt(true);
        // }
      });
    }
  }, [user]);

  // Subscribe to new notifications and show mobile notifications
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToNotifications(user.id, async (newNotification) => {
      // Add to local notifications state
      setNotifications(prev => [newNotification, ...prev.slice(0, 49)]); // Keep max 50

      // Show mobile notification
      await showGameNotification(newNotification);
    });

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUpcomingReminders([]);
      notificationsLoadedRef.current = false;
      notifiedIdsRef.current = new Set();
      return;
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showNotifications) return;
      const panel = notificationPanelRef.current;
      const button = notificationButtonRef.current;
      if (
        panel &&
        !panel.contains(event.target) &&
        button &&
        !button.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifications]);

  useEffect(() => {
    const isMobile = Capacitor.isNativePlatform();

    // Process custom-scheme OAuth callbacks (pista://...#access_token=) and
    // HTTPS universal/app links (https://<host>/verify-email?token=... etc.)
    const processDeepLink = (url) => {
      try {
        debugLog('Processing deep link:', url);
        if (!url) return;

        // 1. Custom scheme: pista:// or pista-test://
        if (OAUTH_CONFIG.isAppSchemeUrl(url)) {
          const hashIndex = url.indexOf('#');
          if (hashIndex === -1) return;

          const hash = url.substring(hashIndex);
          debugLog('Extracted hash from custom-scheme deep link:', hash);

          localStorage.removeItem('oauth_redirect_pending');

          if (window.location.pathname === '/login' && window.location.hash.includes('access_token=')) {
            debugLog('Already on login with access token, avoiding redirect');
            return;
          }

          if (window.location.pathname === '/login') {
            window.location.hash = hash;
          } else {
            window.location.replace('/login' + hash);
          }
          return;
        }

        // 2. HTTPS universal links / Android App Links / iOS Universal Links
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'https:') return;

        const universalPaths = ['/verify-email', '/reset-password', '/reactivate-account'];
        if (OAUTH_CONFIG.isRedirectPage(url) && universalPaths.some(p => urlObj.pathname === p || urlObj.pathname.startsWith(p + '/'))) {
          const target = urlObj.pathname + urlObj.search;
          debugLog('Universal/app link to route:', target);
          window.location.replace(target);
        }
      } catch (err) {
        logger.error('Error processing deep link:', err);
        // Don't crash the app, just continue
      }
    };

    // Listen for deep links (app opened via custom URL scheme)
    let appUrlListener = null;
    if (isMobile) {
      appUrlListener = CapacitorApp.addListener('appUrlOpen', (event) => {
        processDeepLink(event.url);
      });
    }

    // Check for OAuth callback (mobile redirect flow from web)
    const checkOAuthCallback = () => {
      try {
        const hash = window.location.hash;
        const url = window.location.href;
        const pathname = window.location.pathname;

        debugLog('Checking OAuth callback:', { hash, url, pathname });

        if (shouldRedirectToLogin({ pathname, hash, isMobile, href: url })) {
          debugLog('OAuth callback detected, redirecting to login');
          window.location.href = '/login' + hash;
        }
      } catch (err) {
        logger.error('Error checking OAuth callback:', err);
        // Don't crash the app, just continue
      }
    };

    checkOAuthCallback();

    // On mobile, also check periodically for OAuth callback (in case app resumes)
    let intervalId = null;
    if (isMobile) {
      // Check every 2 seconds for OAuth callback when on mobile
      intervalId = setInterval(() => {
        try {
          checkOAuthCallback();
        } catch (err) {
          logger.error('Error in periodic OAuth check:', err);
          // Don't crash the app, just continue
        }
      }, 2000);
    }

    // Initialize auth from Capacitor storage (mobile app persistence)
    authService.restoreFromCapacitor().then(() => {
      checkAuth();
    });

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (appUrlListener) {
        appUrlListener.remove();
      }
    };
  }, []);

  const parseReminderDate = useCallback((value) => {
    if (!value) return null;
    const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const refreshNotifications = useCallback(async (reset = true) => {
    if (!user) return;
    try {
      const offset = reset ? 0 : notificationOffset;
      const limit = 5;

      const res = await httpRequest(`${API_BASE}/notifications?limit=${limit}&offset=${offset}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const items = await res.json();

      if (reset) {
        setNotifications(items || []);
        setNotificationOffset(0);
        notificationsRef.current = items || [];
      } else {
        setNotifications(prev => [...prev, ...(items || [])]);
        setNotificationOffset(offset + items.length);
        notificationsRef.current = [...(notificationsRef.current || []), ...(items || [])];
      }

      setHasMoreNotifications(items.length === limit);
      notificationsLoadedRef.current = true;
    } catch (err) {
      // ignore
    }
  }, [user, notificationOffset]);

  const refreshReminders = useCallback(async () => {
    if (!user) return;
    try {
      const res = await httpRequest(`${API_BASE}/reminders?completed=false`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const reminders = await res.json();
      const now = Date.now();
      const upcoming = [];
      const due = [];
      for (const reminder of reminders) {
        const reminderDate = parseReminderDate(reminder.reminder_datetime);
        if (!reminderDate) continue;
        if (reminderDate.getTime() > now) {
          upcoming.push(reminder);
        } else {
          due.push(reminder);
        }
      }
      setUpcomingReminders(upcoming);

      for (const reminder of due) {
        try {
          await httpRequest(`${API_BASE}/notifications/from-reminder`, {
            method: "POST",
            headers: {
              ...authService.getAuthHeaders(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ reminder_id: reminder.id }),
          });
        } catch (err) {
          // ignore
        }
      }
    } catch (err) {
      // ignore reminder polling errors
    }
  }, [user, parseReminderDate]);

  useEffect(() => {
    if (!user) return undefined;
    refreshNotifications();
    refreshReminders();
    const intervalId = setInterval(() => {
      refreshReminders();
      refreshNotifications();
    }, 60000);
    return () => clearInterval(intervalId);
  }, [user, refreshNotifications, refreshReminders]);

  useEffect(() => {
    if (!user) return;
    ensurePushSubscription();
  }, [user, notificationPermission, ensurePushSubscription]);

  useEffect(() => {
    if (!notificationsLoadedRef.current) {
      notificationsRef.current = notifications;
      return;
    }
    const existingIds = new Set(notificationsRef.current.map((item) => item.id));
    const newNotifications = notifications.filter((item) => !existingIds.has(item.id));
    if (newNotifications.length > 0) {
      for (const notification of newNotifications) {
        if (notifiedIdsRef.current.has(notification.id)) continue;
        notifiedIdsRef.current.add(notification.id);
        showSystemNotification(notification);
      }
    }
    notificationsRef.current = notifications;
  }, [notifications, showSystemNotification]);

  const checkAuth = async () => {
    try {
      // Restore from Capacitor on mobile, then read from localStorage
      await authService.restoreFromCapacitor();
      const token = authService.getToken();
      let userData = null;

      if (token) {
        // Check if token is expired
        if (authService.isTokenExpired()) {
          authService.removeToken();
        } else {
          try {
            userData = await authService.getCurrentUser();
            if (userData) {
              setUser(userData);
            } else {
              authService.removeToken();
            }
          } catch (err) {
            authService.removeToken();
          }
        }
      } else {
        // On web, the httpOnly cookie may be valid even if no token in localStorage
        // Always check with the backend to detect an active session
        try {
          userData = await authService.getCurrentUser();
          if (userData) {
            setUser(userData);
          }
        } catch {
          // No active session — silently ignore
        }
      }

      // Note: Quick Start is triggered in handleLogin, not here
      // This prevents modal from appearing on every page reload

      setLoading(false);
    } catch(err) {
      setLoading(false);
    }
  };

  const handleLogin = async (isNewUser = false) => {
    const userData = await authService.getCurrentUser();

    // Set username to login ID if it's first login or if username is blank
    if (userData && (!userData.username || userData.username.trim() === '')) {
      try {
        // Use email as username fallback, or use the ID if email is not available
        const loginId = userData.email || userData.id.toString();
        await authService.updateUsername(loginId);

        // Update the user data with the new username
        userData.username = loginId;
      } catch (error) {
        logger.warn('Failed to set username on login:', error);
        // Continue with login even if username update fails
      }
    }

    setUser(userData);
    track(isNewUser ? 'user_registered' : 'login', { user_id: userData?.id, provider: userData?.oauth_provider });

    // Trigger Quick Start check for new users or if feature flag is enabled
    if (isFeatureEnabled('QUICK_START_ENABLED')) {
      // Small delay to allow user state to settle
      setTimeout(() => {
        quickStart.checkAndShow();
      }, 500);
    }

    return userData;
  };

  // Callback to update user when BGG ID changes
  const handleUserUpdate = async () => {
    const userData = await authService.getCurrentUser();
    if (userData) {
      setUser(userData);
    }
  };

  const handleLogout = async () => {
    track('logout');
    await analyticsService.flushPending();
    await authService.logout();
    setUser(null);
    // Reset Quick Start so it shows on next login if needed
    quickStart.reset();
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  // Always render GoogleOAuthProvider (required for useGoogleLogin hook)
  // Use placeholder if no client ID is configured - button will be hidden in Login component
  try {
    return (
      <ErrorBoundary
        componentName="App"
        logProps={false}
        fallback={({ errorId, onRetry, onReload }) => (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            padding: '2rem',
            backgroundColor: '#f5f5f5',
            textAlign: 'center',
          }}>
            <h1 style={{ color: '#d32f2f', marginBottom: '1rem' }}>
              Pista App Error
            </h1>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              We&apos;re sorry, but the app encountered an unexpected error.
              <br />
              Error ID: <code style={{ backgroundColor: '#f1f3f4', padding: '2px 6px', borderRadius: '3px' }}>{errorId}</code>
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                onClick={onRetry}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
              <button
                onClick={onReload}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#636e72',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Reload App
              </button>
            </div>
          </div>
        )}
      >
        <GoogleOAuthProvider clientId={googleClientId || 'placeholder-for-hook-compatibility'}>
          <SidebarZIndexProvider>
            <AnalyticsProvider>
            <ChatbotProvider>
            <Router>
              <AppShell
              user={user}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              notificationButtonRef={notificationButtonRef}
              notificationListRef={notificationListRef}
              requestNotificationPermission={requestNotificationPermission}
              setShowNotifications={setShowNotifications}
              showNotifications={showNotifications}
              notificationPanelRef={notificationPanelRef}
              notificationPermission={notificationPermission}
              notifications={notifications}
              totalNotificationCount={totalNotificationCount}
              acknowledgeNotificationReminder={acknowledgeNotificationReminder}
              snoozeNotification={snoozeNotification}
              removeNotification={removeNotification}
              upcomingReminders={upcomingReminders}
              completeReminder={completeReminder}
              clearNotifications={clearNotifications}
              setShowProfileSidebar={setShowProfileSidebar}
              showProfilePanel={showProfilePanel}
              setShowProfilePanel={setShowProfilePanel}
              handleLogout={handleLogout}
              hasMoreNotifications={hasMoreNotifications}
              refreshNotifications={refreshNotifications}
              openModal={openModal}
              showWhyModal={showWhyModal}
              closeModal={closeModal}
            >
              <Routes>
                <Route
                  path="/login"
                  element={
                    user ? (
                      isEmailVerified(user) ? (
                        <Navigate to={getLoginRedirectPath(user)} replace />
                      ) : (
                        <Navigate
                          to={`/verify-email?mode=pending&email=${encodeURIComponent(user.email || "")}`}
                          replace
                        />
                      )
                    ) : (
                      <Login onLogin={handleLogin} />
                    )
                  }
                />
                <Route
                  path="/verify-email"
                  element={<VerifyEmail onLogin={handleLogin} />}
                />
                <Route
                  path="/forgot-password"
                  element={<ForgotPassword />}
                />
                <Route
                  path="/reset-password"
                  element={<ResetPassword />}
                />
                <Route
                  path="/oauth-callback"
                  element={<OAuthCallback />}
                />
                <Route
                  path="/"
                  element={
                    <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Something went wrong</h2><p>The Explore feature encountered an error. Please try refreshing the page.</p></div>}>
                      <Explore user={user} onOpenProfileForBgg={() => setShowProfileSidebar(true)} />
                    </ErrorBoundary>
                  }
                />
                <Route path="/legal" element={<LegalPage />} />

                <Route
                  path="/quick-decision"
                  element={
                    <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Something went wrong</h2><p>The Jet Set Go feature encountered an error. Please try refreshing the page.</p></div>}>
                      {user ? (
                        <Simplify user={user} />
                      ) : (
                        <Simplify user={null} />
                      )}
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Something went wrong</h2><p>The Explore feature encountered an error. Please try refreshing the page.</p></div>}>
                      <Explore user={user} onOpenProfileForBgg={() => setShowProfileSidebar(true)} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/curate"
                  element={<Navigate to="/" replace />}
                />
                <Route
                  path="/collection"
                  element={
                    <VerifiedRoute user={user}>
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Something went wrong</h2><p>The Library feature encountered an error. Please try refreshing the page.</p></div>}>
                        <Collection user={user} />
                      </ErrorBoundary>
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/explore"
                  element={
                    <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Something went wrong</h2><p>The Explore feature encountered an error. Please try refreshing the page.</p></div>}>
                      <Explore user={user} onOpenProfileForBgg={() => setShowProfileSidebar(true)} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/past-sessions"
                  element={
                    <VerifiedRoute user={user}>
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Something went wrong</h2><p>Past Sessions encountered an error. Please try refreshing the page.</p></div>}>
                        <PastSessions user={user} />
                      </ErrorBoundary>
                    </VerifiedRoute>
                  }
                />
                <Route
                  path="/admin/dashboard"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Dashboard encountered an error.</p></div>}>
                        <AdminDashboardPage user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Games admin encountered an error.</p></div>}>
                        <AdminGames user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/feedback"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Feedback admin encountered an error.</p></div>}>
                        <FeedbackAdmin user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/data-updates"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Data updates admin encountered an error.</p></div>}>
                        <AdminDataUpdates user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/ab-test"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>A/B Test admin encountered an error.</p></div>}>
                        <ABTestAdmin user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/feature-blacklist"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Feature Blacklist admin encountered an error.</p></div>}>
                        <FeatureBlacklistAdmin user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/surveys"
                  element={canAccessAdminRoutes(user) ? (
                    <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Survey admin encountered an error.</p></div>}>
                      <SurveyAdmin />
                    </ErrorBoundary>
                  ) : <Navigate to="/" replace />}
                />
                <Route
                  path="/admin/analytics"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Analytics dashboard encountered an error.</p></div>}>
                        <AnalyticsDashboard user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/user-actions"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>User actions admin encountered an error.</p></div>}>
                        <UserActionStats user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>User admin encountered an error.</p></div>}>
                        <UserAdmin user={user} />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route
                  path="/admin/bgg-hotness-similar"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>BGG hotness similar games admin encountered an error.</p></div>}>
                        <AdminBggHotnessSimilar />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route path="/s/:slug" element={<SurveyRoute />} />
                <Route path="/survey" element={<TypeformSurvey user={user} onPersonaUpdate={handleUserUpdate} />} />
                <Route
                  path="/admin/survey-analytics"
                  element={
                    canAccessAdminRoutes(user) ? (
                      <ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Admin Error</h2><p>Survey analytics encountered an error.</p></div>}>
                        <SurveyAnalytics />
                      </ErrorBoundary>
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route path="/share/chart" element={<SharedChart />} />
                <Route path="/session/:sessionId" element={<SessionSharePage />} />
                <Route path="/h/:slug/:range/:mode" element={<HistoryShareRedirect />} />
                <Route path="/h/:slug" element={<HistoryShareRedirect />} />
                <Route path="/session-invite/:token" element={<SessionInvitePage />} />
                <Route path="/reactivate-account" element={<ReactivateAccountPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <PWAInstallPrompt />
              <AppUpdateBanner />
              {/* TODO: Re-enable when MAU reaches 2,000 */}
              {/* <CookieConsentBanner /> */}

              {/* Username reminder: existing users whose username still matches their email */}
              <UsernameReminderBanner
                show={showUsernameReminder}
                userId={user?.id}
                onOpenProfile={() => {
                  setFocusUsernameOnProfileOpen(true);
                  setShowProfileSidebar(true);
                }}
              />

              {/* Notification Permission Prompt */}
              {showNotificationPermissionPrompt && (
                <div
                  style={{
                    position: "fixed",
                    bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))",
                    left: "1rem",
                    right: "1rem",
                    backgroundColor: "var(--bg-primary, white)",
                    border: "1px solid var(--primary-color, #1976d2)",
                    borderRadius: "8px",
                    padding: "1rem",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 1000,
                    maxWidth: "400px",
                    margin: "0 auto"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", marginBottom: "1rem" }}>
                    <span style={{ fontSize: "1.2rem" }}>{NOTIFICATION_ICON}</span>
                    <div>
                      <strong>Enable Notifications</strong>
                      <p style={{ margin: "0.5rem 0", fontSize: "0.9rem", color: "var(--text-secondary, #666)" }}>
                        Stay updated with game reminders and session notifications. Enable notifications to never miss a game night!
                      </p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                    <button
                      onClick={() => setShowNotificationPermissionPrompt(false)}
                      style={{
                        padding: "0.5rem 1rem",
                        border: "1px solid var(--border-color, #ddd)",
                        borderRadius: "4px",
                        backgroundColor: "var(--bg-primary, white)",
                        color: "var(--text-primary, #333)",
                        cursor: "pointer",
                        fontSize: "0.9rem"
                      }}
                    >
                      Not now
                    </button>
                    <button
                      onClick={requestNotificationPermission}
                      style={{
                        padding: "0.5rem 1rem",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor: "var(--primary-color, #1976d2)",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "0.9rem"
                      }}
                    >
                      Enable
                    </button>
                  </div>
                </div>
              )}

            {showProfileSidebar && user && (
              <ProfileSidebar
                user={user}
                notifications={notifications}
                onUserUpdate={handleUserUpdate}
                onClose={() => {
                  setShowProfileSidebar(false);
                  setFocusUsernameOnProfileOpen(false);
                }}
                onLogout={handleLogout}
                removeNotification={removeNotification}
                clearNotifications={clearNotifications}
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                onQuickImport={quickStart.launch}
                focusUsername={focusUsernameOnProfileOpen}
              />
            )}

            {/* RAG Chatbot */}
            {isFeatureEnabled('CHATBOT_ENABLED') && <ChatbotButton />}
            <ChatbotSidebar />

            {/* Quick Start Onboarding Modal */}
            {isFeatureEnabled('QUICK_START_ENABLED') && (
              <QuickStartModal quickStart={quickStart} user={user} onUserUpdate={handleUserUpdate} />
            )}

            {/* Legal document consent prompt */}
            {legalConsentPrompt && user && (
              <LegalConsentPopup
                version={legalConsentPrompt.version}
                summary={legalConsentPrompt.summary}
                effectiveDate={legalConsentPrompt.effectiveDate}
                onAccepted={() => setLegalConsentPrompt(null)}
                onDismiss={() => setLegalConsentPrompt(null)}
              />
            )}

            </AppShell>
          </Router>
          </ChatbotProvider>
          </AnalyticsProvider>
        </SidebarZIndexProvider>
      </GoogleOAuthProvider>
      </ErrorBoundary>
    );
  } catch(e) {
    return <div>Error loading app: {e.message}</div>;
  }
}

export default App;
